/**
 * Composant de sélection de groupe dans le créateur de sondage
 * Permet de sélectionner un groupe existant ou de sauvegarder la liste actuelle
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGroups } from "@core/hooks/useGroups";
import { Users, Save, ChevronDown, LogIn } from "lucide-react";
import { useToast } from "@core/hooks/use-toast";
import { useAuth } from "@core/contexts/AuthContext";
import { PollData } from "@core/hooks/usePolls";
import {
  PendingPollService,
  PendingPollData,
} from "@core/services/PendingPollService";
import { useNavigate } from "react-router-dom";

interface GroupSelectorProps {
  currentEmails: string[];
  onSelectGroup: (
    emails: string[],
    group?: { id: string; name: string },
  ) => void;
  onSaveAsGroup?: (groupId: string) => void;
  currentPollData?: PollData;
  selectedGroupId?: string;
}

export function GroupSelector({
  currentEmails,
  onSelectGroup,
  onSaveAsGroup,
  currentPollData,
  selectedGroupId,
}: GroupSelectorProps) {
  const { t } = useTranslation("polls");
  const { user } = useAuth();
  const { groups, createGroup, loading } = useGroups();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [saving, setSaving] = useState(false);

  // Fonction de validation d'email avec regex
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Fonction pour valider tous les emails et retourner les erreurs
  const validateEmails = (
    emails: string[],
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    emails.forEach((email, index) => {
      if (!validateEmail(email)) {
        if (email.length === 0) {
          errors.push(t("groups.email_error_empty", { index: index + 1 }));
        } else if (!email.includes("@")) {
          errors.push(t("groups.email_error_missing_at", { email }));
        } else if (!email.includes(".")) {
          errors.push(t("groups.email_error_missing_domain", { email }));
        } else if (email.startsWith("@")) {
          errors.push(t("groups.email_error_missing_name", { email }));
        } else if (email.endsWith("@")) {
          errors.push(
            t("groups.email_error_missing_domain_after_at", { email }),
          );
        } else if (email.includes("@@")) {
          errors.push(t("groups.email_error_double_at", { email }));
        } else {
          errors.push(t("groups.email_error_invalid_format", { email }));
        }
      }
    });

    return { isValid: errors.length === 0, errors };
  };

  const handleLoginWithSave = () => {
    if (!currentPollData) {
      toast({
        title: t("groups.no_poll_to_save_title"),
        description: t("groups.no_poll_to_save_description"),
        variant: "destructive",
      });
      return;
    }

    // Déterminer le type de sondage
    const pollType = currentPollData.type || "date";

    const pendingPoll: PendingPollData = {
      pollData: currentPollData,
      timestamp: Date.now(),
      returnPath: window.location.pathname,
      pollType: pollType as "date" | "form" | "quizz" | "availability",
    };

    // Sauvegarder dans sessionStorage
    PendingPollService.savePendingPoll(pendingPoll);

    // Toast rassurant
    toast({
      title: t("groups.poll_saved_title"),
      description: t("groups.poll_saved_description"),
      duration: 3000,
    });

    // Rediriger vers login après 1s
    setTimeout(() => {
      navigate("/login");
    }, 1000);
  };

  const handleSelectGroup = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (group) {
      onSelectGroup(group.member_emails, { id: group.id, name: group.name });
      toast({
        title: t("groups.group_selected_title"),
        description: t("groups.group_selected_description", {
          count: group.member_emails.length,
          name: group.name,
        }),
      });
    }
  };

  const handleSaveAsGroup = async () => {
    if (!newGroupName.trim()) {
      toast({
        title: t("groups.name_required_title"),
        description: t("groups.name_required_description"),
        variant: "destructive",
      });
      return;
    }

    if (currentEmails.length === 0) {
      toast({
        title: t("groups.no_email_title"),
        description: t("groups.no_email_description"),
        variant: "destructive",
      });
      return;
    }

    // Validation des formats d'email
    const emailValidation = validateEmails(currentEmails);
    if (!emailValidation.isValid) {
      const errorMessage =
        emailValidation.errors.length > 3
          ? t("groups.invalid_emails_summary", {
              count: emailValidation.errors.length,
            })
          : t("groups.invalid_emails_list", {
              errors: emailValidation.errors.join(", "),
            });

      toast({
        title: t("groups.invalid_email_format_title"),
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const newGroup = await createGroup({
        name: newGroupName.trim(),
        member_emails: currentEmails,
      });

      if (newGroup) {
        toast({
          title: t("groups.group_saved_title"),
          description: t("groups.group_saved_description", {
            name: newGroup.name,
            count: newGroup.member_emails.length,
          }),
        });
        setShowSaveDialog(false);
        setNewGroupName("");
        onSaveAsGroup?.(newGroup.id);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("groups.save_failed_fallback");

      toast({
        title: t("groups.error_title"),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Si l'utilisateur n'est pas connecté, afficher un message d'information
  if (!user) {
    return (
      <div className="p-4 border-2 border-blue-300 dark:border-blue-700 rounded-md bg-blue-50 dark:bg-blue-900/20">
        <div className="flex items-start gap-3">
          <Save className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
              💾 {t("groups.save_groups_title")}
            </h4>
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
              {t("groups.save_groups_description")}{" "}
              <strong>{t("groups.save_groups_reassurance")}</strong>
            </p>
            <button
              onClick={handleLoginWithSave}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 mb-2"
              data-testid="login-to-save-button"
            >
              <LogIn className="w-4 h-4" />
              {t("groups.login_to_save")}
            </button>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              {t("groups.login_to_save_hint")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Sélection d'un groupe existant */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <Users className="inline w-4 h-4 mr-1" />
          {t("groups.use_existing_group")}
        </label>
        <div className="relative">
          <select
            className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none"
            onChange={(e) => {
              if (e.target.value) {
                handleSelectGroup(e.target.value);
              }
            }}
            disabled={loading}
            value={selectedGroupId || ""}
          >
            <option value="">{t("groups.select_group_placeholder")}</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>
                {t("groups.group_option", {
                  name: group.name,
                  count: group.member_emails.length,
                })}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        {groups.length === 0 && !loading && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t("groups.no_saved_groups")}
          </p>
        )}
      </div>

      {/* Bouton pour sauvegarder la liste actuelle */}
      {currentEmails.length > 0 && user && (
        <div>
          {!showSaveDialog ? (
            <button
              type="button"
              onClick={() => setShowSaveDialog(true)}
              className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {t("groups.save_as_new_group")}
            </button>
          ) : (
            <div className="p-4 border-2 border-purple-300 dark:border-purple-700 rounded-md bg-purple-50 dark:bg-purple-900/20 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t("groups.group_name_label")}
                </label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t("groups.group_name_placeholder")}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSaveAsGroup}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? t("groups.saving") : t("groups.save")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSaveDialog(false);
                    setNewGroupName("");
                  }}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  {t("groups.cancel")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
